import { Card, CardBody, EmptyState, SkeletonRows } from '@ambernord/ui';
import { CircleAlert, Inbox } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../lib/api.js';
import { AppHeader } from './AppHeader.js';
import { CommandPalette } from './CommandPalette.js';
import { Sidebar } from './Sidebar.js';
import { activeNavFor } from './nav.js';

/**
 * Lietotnes čaula: sakļaujama sānjosla (desktop) / atvilktne (mobilais) + augšjosla
 * ar ⌘K paleti un lietotāja izvēlni. Skaitītāju nozīmītes baro GET /api/dashboard/summary
 * (`counts: { currentItems, todos, missingArtifacts, overdue }`) — ielādē montējot,
 * atsvaidzina ik 60 s.
 */

const SIDEBAR_KEY = 'ambernord_sidebar_collapsed';
const SUMMARY_POLL_MS = 60_000;

interface SummaryResponse {
  scope?: 'platform' | 'supplier' | 'carrier' | 'customer';
  counts?: {
    currentItems?: number;
    todos?: number;
    missingArtifacts?: number;
    overdue?: number;
  };
}

export function AppLayout({ title, children }: { title: string; children: ReactNode }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_KEY) === '1');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      localStorage.setItem(SIDEBAR_KEY, c ? '0' : '1');
      return !c;
    });
  }, []);

  // ⌘K / Ctrl+K → komandpalete.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Maršruta maiņa aizver mobilo atvilktni.
  // biome-ignore lint/correctness/useExhaustiveDependencies: apzināti reaģē uz pathname
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Nozīmīšu skaitītāji: ielādē montējot + atsvaidzina ik 60 s.
  useEffect(() => {
    if (!user) return;
    let active = true;
    const load = async () => {
      const res = await api.get<SummaryResponse>('/api/dashboard/summary');
      if (active && res.ok) setSummary(res.data);
    };
    void load();
    const id = window.setInterval(() => void load(), SUMMARY_POLL_MS);
    return () => {
      active = false;
      window.clearInterval(id);
    };
  }, [user]);

  // counts → nav ceļi. `currentItems` klienta tvērumā ir gala pasūtījumi (/orders),
  // citādi piegādes pasūtījumi (/supply-orders). `missingArtifacts` → Dokumenti,
  // `todos` → Uzdevumi, `overdue` → Dashboard.
  const badges = useMemo<Record<string, number>>(() => {
    const c = summary?.counts;
    if (!c) return {};
    const out: Record<string, number> = {};
    const currentTarget = summary?.scope === 'customer' ? '/orders' : '/supply-orders';
    if (c.currentItems) out[currentTarget] = c.currentItems;
    if (c.todos) out['/tasks'] = c.todos;
    if (c.missingArtifacts) out['/documents'] = c.missingArtifacts;
    if (c.overdue) out['/'] = c.overdue;
    return out;
  }, [summary]);

  const active = activeNavFor(pathname);
  const heading = active ? t(`nav.${active.item.key}`) : title;
  const crumb = active
    ? `${t(`navGroups.${active.group.key}`)} / ${t(`nav.${active.item.key}`)}`
    : null;

  return (
    <div className="flex min-h-full bg-slate-50 dark:bg-slate-950">
      <Sidebar
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        badges={badges}
      />
      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        <AppHeader
          showSignOut
          onToggleSidebar={() => setMobileOpen((o) => !o)}
          onOpenPalette={() => setPaletteOpen(true)}
        />
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 lg:px-8">
          <div className="mb-6">
            {crumb ? (
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                {crumb}
              </p>
            ) : null}
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
              {heading}
            </h1>
          </div>
          {children}
        </main>
        <footer className="border-t border-slate-200 py-4 text-center text-xs text-slate-400 dark:border-slate-800">
          © {new Date().getFullYear()} Matisons Selection OÜ — AmberNord · B2B Portal
        </footer>
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}

/** Vienkāršs datu stāvokļa palīgs lapām (nemainīga signatūra — visas lapas to importē). */
export function DataState({
  loading,
  error,
  empty,
  children,
}: {
  loading: boolean;
  error: string | null;
  empty: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  if (loading) return <SkeletonRows rows={5} />;
  if (error)
    return (
      <Card className="border-red-200 dark:border-red-900/60">
        <CardBody className="flex items-start gap-3 p-4">
          <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-500" aria-hidden />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-red-700 dark:text-red-400">
              {t('status.error')}
            </p>
            <p className="mt-0.5 break-words text-sm text-slate-600 dark:text-slate-400">{error}</p>
          </div>
        </CardBody>
      </Card>
    );
  if (empty)
    return (
      <EmptyState
        icon={<Inbox className="h-6 w-6" aria-hidden />}
        title={t('status.empty')}
        hint={t('status.emptyHint')}
      />
    );
  return <>{children}</>;
}
